import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllVotes,
  getUserBallots,
} from "../../../../services/voteServices";
import {
  filterVotesBySearch,
  getStoredVotesUi,
  isVoteVisibleToUser,
  saveStoredVotesUi,
} from "../utils/voteUserUtils";

export function useUserVotesController({ userId }) {
  const storedUi = useMemo(() => getStoredVotesUi(), []);

  const [votes, setVotes] = useState([]);
  const [ballots, setBallots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState(storedUi.searchTerm || "");
  const [viewFilter, setViewFilter] = useState(storedUi.viewFilter || "all");

  useEffect(() => {
    saveStoredVotesUi({ searchTerm, viewFilter });
  }, [searchTerm, viewFilter]);

  const fetchVotesAndBallots = useCallback(async () => {
    if (!userId) {
      setVotes([]);
      setBallots([]);
      setLoading(false);
      setError("Utilisateur non identifié.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const [voteData, ballotData] = await Promise.all([
        getAllVotes(),
        getUserBallots(userId),
      ]);

      const activeVotes = Array.isArray(voteData)
        ? voteData.filter(isVoteVisibleToUser)
        : [];

      setVotes(activeVotes);
      setBallots(Array.isArray(ballotData) ? ballotData : []);
    } catch (err) {
      console.error("Erreur chargement votes/réponses :", err);
      setVotes([]);
      setBallots([]);
      setError("Erreur lors du chargement des votes ou des réponses.");
      toast.error("Erreur lors du chargement des votes ou réponses");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchVotesAndBallots();
  }, [fetchVotesAndBallots]);

  const ballotsByVoteId = useMemo(() => {
    const map = new Map();

    ballots.forEach((ballot) => {
      if (ballot?.vote_id) {
        map.set(ballot.vote_id, ballot);
      }
    });

    return map;
  }, [ballots]);

  const hasBallot = useCallback(
    (voteId) => ballotsByVoteId.get(voteId) || null,
    [ballotsByVoteId]
  );

  const filteredVotes = useMemo(() => {
    let nextVotes = filterVotesBySearch(votes, searchTerm);

    if (viewFilter === "pending") {
      nextVotes = nextVotes.filter((vote) => !ballotsByVoteId.has(vote.id));
    }

    if (viewFilter === "answered") {
      nextVotes = nextVotes.filter((vote) => ballotsByVoteId.has(vote.id));
    }

    return nextVotes;
  }, [ballotsByVoteId, searchTerm, viewFilter, votes]);

  const stats = useMemo(() => {
    const answered = votes.filter((vote) => ballotsByVoteId.has(vote.id)).length;
    const total = votes.length;
    const pending = Math.max(total - answered, 0);

    return {
      total,
      answered,
      pending,
      filtered: filteredVotes.length,
    };
  }, [ballotsByVoteId, filteredVotes.length, votes]);

  return {
    votes,
    ballots,
    loading,
    error,

    searchTerm,
    setSearchTerm,
    viewFilter,
    setViewFilter,

    filteredVotes,
    stats,
    hasBallot,
    fetchVotesAndBallots,
  };
}